import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { useDangerousLatestValueRef } from '../useLatestValueRef';
import { InputFormatter, stringFormatter } from './formatters';

type AnyInputEvent = { currentTarget: { value: string } };

interface UseInputFieldOptions<T> {
  /** External/source value (store, prop). */
  value: T | null | undefined;
  /** Converts between the source value and the draft string. Defaults to identity. */
  formatter?: InputFormatter<T>;
  /** 'blur' commits on blur; 'change' also commits live on every keystroke. */
  commitOn?: 'blur' | 'change';
  /** Receives the parsed value (or `null` when unparseable) when committed. */
  onCommit?: (parsed: T | null) => void;
}

/**
 * Returns the props (`value`, `onFocus`, `onChange`, `onBlur`) for a controlled
 * text `<input>` / `<textarea>`, backed by a local draft string so the rendered
 * `value` always matches what the user typed. In Preact the DOM `value` is only
 * rewritten when `newProps.value !== dom.value` (see preact/src/diff/index.js), so
 * a draft that updates synchronously with each keystroke never dislodges the caret
 * — even when the committed value round-trips through a lagging store. While focused
 * the external `value` is re-synced into the draft only when it differs from the echo
 * of our own last commit, so an in-progress edit is never clobbered by its own
 * round-trip yet a genuine external change (an autocomplete selection, a parent
 * reset) still shows up live.
 *
 * Spread the result onto the input (`<input {...field} />`) or pass the props
 * individually.
 */
const useInputField = <T = string>({
  value,
  formatter = stringFormatter as unknown as InputFormatter<T>,
  commitOn = 'blur',
  onCommit,
}: UseInputFieldOptions<T>) => {
  // Keep the latest formatter/callback without re-creating handlers or re-running
  // the sync effect when callers pass inline functions.
  const formatterRef = useDangerousLatestValueRef(formatter);
  const onCommitRef = useDangerousLatestValueRef(onCommit);

  const [draft, setDraft] = useState<string>(() => formatter.format(value));
  const focusedRef = useRef(false);
  // The formatted external value our own last commit produced. Used to tell an
  // echo of our edit apart from a genuine external change while focused.
  const lastSyncRef = useRef<string>(draft);

  // Re-sync from the external value. While focused we adopt it only when it isn't
  // the echo of our own commit — so a lossy parse/format round-trip (e.g. "1." -> 1
  // -> "1") never rewrites the caret, but a real external change (an autocomplete
  // selection, a parent reset) still shows up live.
  useEffect(() => {
    const next = formatterRef.current.format(value);
    if (!focusedRef.current || next !== lastSyncRef.current) {
      lastSyncRef.current = next;
      setDraft(next);
    }
  }, [value, formatterRef]);

  const commit = useCallback(
    (raw: string) => {
      const parsed = formatterRef.current.parse(raw);
      lastSyncRef.current = formatterRef.current.format(parsed);
      onCommitRef.current?.(parsed);
    },
    [onCommitRef, formatterRef],
  );

  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const onChange = useCallback(
    (event: AnyInputEvent) => {
      const next = event.currentTarget.value;
      setDraft(next);
      if (commitOn === 'change') {
        commit(next);
      }
    },
    [commitOn, commit],
  );

  const onBlur = useCallback(
    (event: AnyInputEvent) => {
      focusedRef.current = false;
      commit(event.currentTarget.value);
    },
    [commit],
  );

  return {
    value: draft,
    onFocus,
    onChange,
    onBlur,
  };
};

export default useInputField;
